/**
 * TosGate — gate di RI-accettazione dei Termini.
 *
 * Scatta SOLO per un utente già dentro (store.sigil presente) che non ha
 * accettato la versione CORRENTE dei Termini (TOS_VERSION). Tipico caso: hai
 * alzato TOS_VERSION dopo un aggiornamento legale → chi è già registrato la
 * rivede e deve riaccettare per continuare.
 *
 * Non scatta in onboarding: là il sigil viene impostato SOLO dopo che
 * SigilScreen ha già registrato l'accettazione (recordTosAcceptanceLocal).
 *
 * Robustezza: se in locale non risulta accettata, controlla il SERVER (l'utente
 * potrebbe aver accettato su un altro device) prima di mostrare il prompt; e in
 * caso di errore di rete NON blocca (niente lock-out).
 */

import { useEffect, useState } from 'react';
import { useStore } from '../lib/store';
import { TOS_VERSION, hasAcceptedCurrentTos, recordTosAcceptanceLocal, acceptTosRemote } from '../lib/legal';
import { getMyAccount } from '../lib/derivedSigil';

export default function TosGate() {
  const sigil = useStore((s) => s.sigil);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!sigil) { setShow(false); return; }
      if (hasAcceptedCurrentTos()) { setShow(false); return; }
      // Locale non aggiornato: forse ha accettato su un altro device → server.
      try {
        const a = await getMyAccount();
        if (cancelled) return;
        const serverV = a?.tosVersion;
        if (serverV === TOS_VERSION) {
          recordTosAcceptanceLocal(); // accettata altrove → sincronizza, niente prompt
          setShow(false);
        } else if (typeof serverV === 'string' && serverV) {
          // Versione registrata NON nulla e diversa = vero cambio di versione → prompt.
          setShow(true);
        } else {
          // tosVersion null/sconosciuta: può essere un legacy senza registrazione
          // o una RACE post-takeover/recovery (getMyAccount parte prima che il
          // server sincronizzi il sigil). Non blocchiamo: evitiamo falsi positivi.
          setShow(false);
        }
      } catch {
        if (!cancelled) setShow(false); // errore rete: non blocchiamo
      }
    })();
    return () => { cancelled = true; };
  }, [sigil]);

  async function onAccept() {
    setBusy(true);
    try { await acceptTosRemote(); } finally { setBusy(false); setShow(false); }
  }

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4 font-mono animate-fade-in">
      <div className="w-full max-w-md border border-ink-dim/50 rounded-2xl bg-black p-5 shadow-glow">
        <div className="text-ink text-glow text-[18px] mb-2">📜 Termini aggiornati</div>
        <p className="text-[14px] text-ink-dim mb-3 leading-relaxed">
          Abbiamo aggiornato i Termini di Servizio e/o l'Informativa Privacy.
          Per continuare a usare Nullum devi rivederli e accettarli.
        </p>
        <div className="flex gap-4 mb-4 text-[14px]">
          <a href="/termini" target="_blank" rel="noopener noreferrer" className="text-ink underline">Termini di Servizio</a>
          <a href="/info" target="_blank" rel="noopener noreferrer" className="text-ink underline">Informativa Privacy</a>
        </div>
        <button
          onClick={onAccept}
          disabled={busy}
          className="w-full border border-ink rounded-lg bg-ink/10 text-ink py-3 text-[15px] hover:bg-ink/20 transition-colors disabled:opacity-50"
        >
          {busy ? '…' : '✓ Ho letto e accetto — continua'}
        </button>
        <p className="text-[11px] text-ink-dim mt-3 text-center">versione {TOS_VERSION}</p>
      </div>
    </div>
  );
}
